<template>
  <div class="my-6 leading-normal">
    <div class="flex flex-wrap -mx-2">
      <div class="flex-col w-full md:w-1/2 px-2">
        <panel class="pt-6 mb-6">
          <address-lookup-input
            label="Property Address"
            v-model="form.property_address"
            @selected="getDetailedAddress"
            :error-bag="errorBag"
          />
        </panel>
      </div>
      <div class="flex-col w-full md:w-1/2 px-2">
        <panel class="pt-6 mb-6">
          <text-input
            v-model="form.price_variance"
            label="Price Variance(%)"
            :error="errorBag.get('price_variance')"
          />
          <text-input
            v-model="form.score_threshold"
            label="Score Threshold"
            :error="errorBag.get('score_threshold')"
          />
        </panel>
      </div>
      <div class="flex-col w-full md:w-1/2 px-2"></div>
      <div class="flex-col w-full md:w-1/2 px-2 text-right">
        <button class="bg-primary rounded px-6 py-3 text-white"
          :loading="loading"
          @click="compare">
          <i class="fas fa-sync fa-fw fa-spin" v-if="loading"></i>
          <span v-else>Compare</span>
        </button>
      </div>
    </div>
    <!-- <loader v-if="results === null" class="mt-4" /> -->
    <!-- <info-msg class="mt-8" v-if="results === null" message="Your search returned no results" /> -->
    <div v-if="results !== null">
      <div class="bg-grey-lighter min-h-screen mt-6">
        <div class="border border-grey bg-white p-4 rounded-md shadow">
          <h2 class="font-bold text-lg">This is your subject property</h2>
          <p class="text-gray-600">{{ results.subjectProperty.postcode }}</p>
          <p class="text-gray-600">{{ getLatestListing(results.subjectProperty.listings).type }} | {{ getLatestListing(results.subjectProperty.listings).bedrooms }} bedrooms</p>
          <p class="text-gray-600">{{ getLatestListing(results.subjectProperty.listings).price | gbp }}</p>
        </div>
        <div class="flex flex-wrap justify-center -mx-3 mt-3 pt-6">
          <div class="w-full md:w-1/3 px-3">
            <fieldset class="border border-green bg-green-lighter p-3 shadow-md h-full flex flex-col rounded-lg">
              <div class="text-center h-3/4">
                <div class="flex-grow">
                  <p class="">
                    Your property looks great compared to <strong>{{ propertyCount(results.activePropertiesCounts.greenCount, results.inactivePropertiesCounts.greenCount) }} other <span v-if="propertyCount(results.activePropertiesCounts.greenCount, results.inactivePropertiesCounts.greenCount) > 1">properties</span><span v-else>property</span></strong> active in the local market.
                  </p>
                </div>
              </div>
              <div class="mt-3 mb-3 text-center text-xs h-1/4">
                <p>{{results.activePropertiesCounts.greenCount}} On-Market</p>
                <p>{{results.inactivePropertiesCounts.greenCount}} Recently Sold / Offered</p>
              </div>
            </fieldset>
          </div>
          <div class="w-full md:w-1/3 px-3">
            <fieldset class="border border-yellow bg-yellow-lighter p-3 shadow-md h-full flex flex-col rounded-lg">
              <div class="text-center h-3/4">
                <div class="flex-grow">
                  <p class="">
                    Your property looks reasonable compared to <strong>{{ propertyCount(results.activePropertiesCounts.yellowCount, results.inactivePropertiesCounts.yellowCount) }} other <span v-if="propertyCount(results.activePropertiesCounts.yellowCount, results.inactivePropertiesCounts.yellowCount) > 1">properties</span><span v-else>property</span></strong> active in the local market.
                  </p>
                </div>
              </div>
              <div class="mt-3 mb-3 text-center text-xs h-1/4">
                <p>{{results.activePropertiesCounts.yellowCount}} On-Market</p>
                <p>{{results.inactivePropertiesCounts.yellowCount}} Recently Sold / Offered</p>
              </div>
            </fieldset>
          </div>
          <div class="w-full md:w-1/3 px-3">
            <fieldset class="border border-red bg-red-lighter p-3 shadow-md h-full flex flex-col rounded-lg">
              <div class="text-center h-3/4">
                <div class="flex-grow">
                  <p class="">
                    We found <strong>{{ propertyCount(results.activePropertiesCounts.redCount, results.inactivePropertiesCounts.redCount) }} <span v-if="propertyCount(results.activePropertiesCounts.redCount, results.inactivePropertiesCounts.redCount) > 1">properties</span><span v-else>property</span></strong> that look better value for money than your property, active in the local market.
                  </p>
                </div>
              </div>
              <div class="mt-3 mb-3 text-center text-xs h-1/4">
                <p>{{results.activePropertiesCounts.redCount}} On-Market</p>
                <p>{{results.inactivePropertiesCounts.redCount}} Recently Sold / Offered</p>
              </div>
            </fieldset>
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 mt-6">
          <div class="w-1/4 px-2 mb-4">
            <div class="border border-grey bg-grey-lighter p-4 rounded-lg shadow">
              <div class="h-24">
              <h5 class="font-bold"><a :href="getLatestListing(results.subjectProperty.listings).url" class="text-black no-underline hover:underline" target="_blank">{{ results.subjectProperty.address }}</a></h5>
              </div>
              <div class="h-12">
                <p class="text-grey">{{ getLatestListing(results.subjectProperty.listings).type }} | {{ getLatestListing(results.subjectProperty.listings).bedrooms }} <span v-if="getLatestListing(results.subjectProperty.listings).bedrooms > 1">bedrooms</span><span v-else>bedroom</span></p>
              </div>
              <div class="border border-grey mt-2 bg-white">
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Price</span>
                  <span>{{ getLatestListing(results.subjectProperty.listings).price | gbp }}</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Property Type</span>
                  <span>{{ getLatestListing(results.subjectProperty.listings).type }}</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Bedrooms</span>
                  <span>{{ getLatestListing(results.subjectProperty.listings).bedrooms }}</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Bathrooms</span>
                  <span v-if="results.subjectProperty.bathroomScore > 0">This property appears to have {{ results.subjectProperty.bathroomScore }} <span v-if="results.subjectProperty.bathroomScore > 1">bathrooms</span><span v-else>bathroom</span></span>
                  <span v-else>There's no indication of a bathroom for this property</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Size</span>
                  <span v-if="results.subjectProperty.floorArea">{{ results.subjectProperty.floorArea }} sq/m</span>
                  <span v-else>?</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Garden</span>
                  <span v-if="results.subjectProperty.gardenKeyword">This property appears to have a {{ results.subjectProperty.gardenKeyword }}</span>
                  <span v-else>Theres no indication of a garden for this property</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Parking</span>
                  <span v-if="results.subjectProperty.parkingKeyword">This property appears to have {{ results.subjectProperty.parkingKeyword }}</span>
                  <span v-else>There's no mention of a parking for this property</span>
                </p>
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Condition</span>
                  <span v-if="results.subjectProperty.conditionKeyword">Property description mentions "{{ results.subjectProperty.conditionKeyword }}"</span>
                  <span v-else>This property does not have any particular mentions of condition.</span>
                </p>
              </div>
            </div>
          </div>
          <div class="w-1/4 px-2 mb-4" v-for="(result,i) in results.activeProperties" :key="i" >
            <div class="border border-grey-lighter bg-white p-4 rounded-lg shadow">
              <div class="h-24">
                <h5 class="font-bold"><a :href="getLatestListing(result.listings).url" class="text-black no-underline hover:underline" target="_blank">{{ result.address }}</a></h5>
              </div>
              <div class="h-12">
                <p class="text-grey">{{result.distance}} miles | {{ getLatestListing(result.listings).type }} | {{ result.bedrooms }} bedrooms</p>
              </div>
              <div class="border border-grey mt-2">
                <p class="border-b p-2 text-center">
                  <span class="font-bold block">Price</span>
                  <span>{{ getLatestListing(result.listings).price | gbp }}</span>
                </p>
                <p class="border-b p-2 text-center" :class="result.competitivePropertyTypeScore > 0 ? 'border border-red bg-red-lighter' : result.competitivePropertyTypeScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Property Type</span>
                  <span>{{ getLatestListing(result.listings).type }}</span>
                </p>
                <p class="border-b p-2 text-center" :class="result.competitiveBedroomScore > 0 ? 'border border-red bg-red-lighter' : result.competitiveBedroomScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Bedrooms</span>
                  <span>{{ result.bedrooms }}</span>
                </p>
                <p class="border-b p-2 text-center"
                  :class="result.comparedBathroomsScore > 0 ? 'border border-red bg-red-lighter' : result.comparedBathroomsScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Bathrooms</span>
                  <span v-if="result.competitiveBathroomScore > 0">This property appears to have {{ result.competitiveBathroomScore }} <span v-if="result.competitiveBathroomScore > 1">bathrooms</span><span v-else>bathroom</span></span>
                  <span v-else>There's no indication of a bathroom for this property</span>
                </p>
                <p class="border-b p-2 text-center"
                  :class="result.competitiveSqftScore > 0 ? 'border border-red bg-red-lighter' : result.competitiveSqftScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Size</span>
                  <span v-if="result.floorArea">{{ result.floorArea }} sq/m (<span v-if="result.floorAreaDifference > 0">+{{ result.floorAreaDifference }}% bigger</span><span v-else>-{{ result.floorAreaDifference }}% smaller</span>)</span>
                  <span v-else>?</span>
                </p>
                <p class="border-b p-2 text-center"
                  :class="result.comparedGardenScore > 0 ? 'border border-red bg-red-lighter' : result.comparedGardenScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Garden</span>
                  <span v-if="result.competitiveGardenKeyword">This property appears to have a {{ result.competitiveGardenKeyword === 'gardens' ? 'multiple gardens' : result.competitiveGardenKeyword }}</span>
                  <span v-else>Theres no indication of a garden for this property</span>
                </p>
                <p class="border-b p-2 text-center"
                  :class="result.comparedParkingScore > 0 ? 'border border-red bg-red-lighter' : result.comparedParkingScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Parking</span>
                  <span v-if="result.competitiveParkingKeyword">This property appears to have {{ result.competitiveParkingKeyword }}</span>
                  <span v-else>There's no mention of a parking for this property</span>
                </p>
                <p class="border-b p-2 text-center"
                  :class="result.comparedConditionScore > 0 ? 'border border-red bg-red-lighter' : result.comparedConditionScore < 0 ? 'border border-green bg-green-lighter' : ''">
                  <span class="font-bold block">Condition</span>
                  <span v-if="result.competitiveConditionKeyword">Property description mentions "{{ result.competitiveConditionKeyword }}"</span>
                  <span v-else>This property does not have any particular mentions of condition.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="items-stretch w-full">
          <div class="w-full">
            <GmapMap
              class="w-full h-120 mb-6 sm:mb-0 rounded overflow-hidden"
              ref="agentsMap"
              :center="{ lat: results.subjectProperty.lat, lng: results.subjectProperty.lng }"
              :zoom="map.zoom"
              :options="map.options">
              <GmapMarker
                :position="{ lat: results.subjectProperty.lat, lng: results.subjectProperty.lng }"
                :icon="{url: map.url, scaledSize: {width: 20, height: 20}}"
                :title="`Your property - ${formatPrice(getLatestListing(results.subjectProperty.listings).price)}`"
                >
              </GmapMarker>
              <GmapMarker
                v-for="(inactiveProperty,index) in results.inactiveProperties"
                :key="index"
                :position="{ lat: inactiveProperty.lat, lng: inactiveProperty.lng }"
                :icon="{url: getMarkerColor(inactiveProperty.finalScore), scaledSize: {width: 20, height: 20}
              }"
                @click="openListingLink(getLatestListing(inactiveProperty.listings).url)"
                :title="`Sold ${inactiveProperty.monthsAgo} months ago - ${formatPrice(getLatestListing(inactiveProperty.listings).price)}`"
              >
              </GmapMarker>
            </GmapMap>
          </div>
        </div>
      </div>
  </div>
  </div>
</template>

<script>

import { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import { mapActions } from 'vuex'
import AddressLookupInput from '../../components/AddressLookupInput.vue'

export default {
  components: { AddressLookupInput },
  data () {
    return {
      loading: false,
      form: {
        property_address: null,
        address_name_number: null,
        address_postcode: null,
        price_variance: 10,
        score_threshold: 10
      },
      results: null,
      errorBag: new ErrorBag(),
      map: {
        zoom: 13,
        options: {},
        url: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'><path d='M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z'/></svg>",
        showByIndex: null
      }
    }
  },
  computed: {
  },
  watch: {
  },
  created: function () {
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    getDetailedAddress (address) {
      this.form.address_name_number = address.address_name_number
      this.form.address_postcode = address.address_postcode
    },
    async compare () {
      this.loading = true
      this.results = null
      this.errorBag.clear()
      await v2Api.get('/property-comparison-tool', { params: this.form }).then((response) => {
        this.results = response.data
        this.loading = false
        this.oldForm = JSON.parse(JSON.stringify(this.form))
        return this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Success',
          info: 'Comparison report has been generated.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.loading = false
        if (errors.response.status === 400) {
          this.displayToastNotification({
            type: 'danger',
            info: errors.response.data.message || 'No listings for this property have been found in our data, please ensure your subject property is currently or recently listed on the market.'
          })
        } else if (errors.response.status !== 422) {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Internal Server Error',
            info: 'Whoops! Looks like something went wrong.'
          })
        }
      })
    },
    getLatestListing (listings) {
      if (!listings || listings.length === 0) return null
      return [...listings].sort((a, b) => new Date(b.listedAt) - new Date(a.listedAt))[0]
    },
    propertyCount (activeCount, inactiveCount) {
      return activeCount + inactiveCount
    },
    getMarkerColor (score) {
      const negativeThreshold = this.form.score_threshold * -1
      const positiveThreshold = this.form.score_threshold
      console.log(score, negativeThreshold)
      if (score <= negativeThreshold) { // green
        return "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'%3E%3Cpath fill='%231f9d55' d='M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z'/%3E%3C/svg%3E"
      } else if (score > negativeThreshold && score < positiveThreshold) { // yellow
        return "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'%3E%3Cpath fill='%23f2d024' d='M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z'/%3E%3C/svg%3E"
      } else if (score >= positiveThreshold) { // red
        return "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'%3E%3Cpath fill='%23cc1f1a' d='M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z'/%3E%3C/svg%3E"
      }
    },
    openListingLink (link) {
      window.open(link, '_blank')
    },
    formatPrice (value) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value)
    }
  }
}
</script>
